import type { ReactNode } from 'react';
import type { CurrentMember } from '../../shared/models';
import type { CreateWorkspaceInput, WorkspaceSummary } from '../../shared/workspaces';
import { useI18n } from '../i18n';
import type { AppRoute, AppScreen } from '../routes';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { TaskCenter } from '../tasks/TaskCenter';
import { QuickCreateMenu } from './QuickCreateMenu';
import { UiIcon, type UiIconName } from './ActionIcon';

export interface ShellAccess {
  recipeView: boolean;
  recipeEdit: boolean;
  recipeExport: boolean;
  menuView: boolean;
  menuEdit: boolean;
  menuExport: boolean;
  dataExportAll: boolean;
  historyRestore: boolean;
  researchView: boolean;
  researchEdit: boolean;
  memberManage: boolean;
  permissionManage: boolean;
  auditView: boolean;
  mediaRestore: boolean;
  mediaPurge: boolean;
  costView: boolean;
  costExport: boolean;
}

interface Props {
  member: CurrentMember;
  environment: string;
  route: AppRoute;
  access: ShellAccess;
  workspaces: WorkspaceSummary[];
  currentWorkspaceId: string;
  canCreateWorkspace: boolean;
  onWorkspaceSwitch: (workspaceId: string) => Promise<void> | void;
  onWorkspaceCreate: (input: CreateWorkspaceInput) => Promise<void>;
  navigate: (route: AppRoute) => void;
  children: ReactNode;
}

const MORE_SCREENS: AppScreen[] = [
  'more', 'ingredients', 'imports', 'scaling', 'exports', 'trash', 'media-recycle',
  'members', 'roles', 'audit', 'workspace-settings', 'account'
];

function routeGroupActive(screen: AppScreen, target: AppScreen): boolean {
  if (target === 'library') return screen === 'library' || screen === 'recipes' || screen === 'dishes';
  if (target === 'more') return MORE_SCREENS.includes(screen);
  return screen === target;
}

const NAV_ICONS: Partial<Record<AppScreen, UiIconName>> = {
  home: 'home',
  library: 'library',
  menus: 'menu',
  more: 'more'
};

export function AppShell({
  member, environment, route, access, workspaces, currentWorkspaceId, canCreateWorkspace,
  onWorkspaceSwitch, onWorkspaceCreate, navigate, children
}: Props) {
  const { locale, t } = useI18n();

  function navButton(target: AppScreen, label: string, allowed = true) {
    if (!allowed) return null;
    const icon = NAV_ICONS[target];
    return <button className={routeGroupActive(route.screen, target) ? 'active' : ''} onClick={() => navigate({ screen: target })}>
      {icon && <span className="shell-nav-icon" aria-hidden="true"><UiIcon name={icon} size={20} /></span>}
      <span className="shell-nav-label">{label}</span>
    </button>;
  }

  return <div className="app-shell" data-screen={route.screen}>
    <aside className="sidebar desktop-sidebar">
      <div className="sidebar-brand"><span className="brand-kicker">{locale === 'zh-CN' ? '厨房工作簿' : 'Kitchen notebook'}</span><h1>Menu Lab</h1><span className="env-badge">{environment}</span></div>
      <WorkspaceSwitcher mode="desktop" workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} canCreate={canCreateWorkspace} onSwitch={onWorkspaceSwitch} onCreate={onWorkspaceCreate} />
      <div className="sidebar-groups">
        <section className="sidebar-group">
          <span className="sidebar-group-label">{locale === 'zh-CN' ? '工作' : 'Work'}</span>
          <nav>
            {navButton('home', t('nav.home'))}
            {navButton('library', t('nav.library'), access.recipeView)}
            {navButton('menus', t('nav.menus'), access.menuView)}
            {navButton('more', t('nav.more'))}
          </nav>
          <QuickCreateMenu placement="desktop" canCreateKitchenItem={access.recipeEdit} canImport={access.researchEdit} canCreateMenu={access.menuEdit} navigate={navigate} />
        </section>
      </div>
      <button className={`sidebar-account-card ${route.screen === 'account' ? 'active' : ''}`} onClick={() => navigate({ screen: 'account' })}>
        <strong>{member.displayName}</strong><span>{member.email}</span><em>{t('nav.account')}</em>
      </button>
    </aside>

    <div className="app-main">
      <header className="mobile-topbar">
        <WorkspaceSwitcher mode="mobile" workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} canCreate={canCreateWorkspace} onSwitch={onWorkspaceSwitch} onCreate={onWorkspaceCreate} />
        <div className="mobile-topbar-actions">
          <TaskCenter navigate={navigate} compact />
          <button className="mobile-profile" onClick={() => navigate({ screen: 'more' })} aria-label={t('nav.more')}>{member.displayName.slice(0, 1).toUpperCase()}</button>
        </div>
      </header>
      <main className="workspace"><div className="route-view" key={`${currentWorkspaceId}:${route.screen}`}>{children}</div></main>
      <nav className="mobile-bottom-nav" aria-label={locale === 'zh-CN' ? '主要导航' : 'Primary navigation'}>
        {navButton('home', t('nav.home'))}
        {navButton('library', t('nav.library'), access.recipeView)}
        <QuickCreateMenu placement="mobile" canCreateKitchenItem={access.recipeEdit} canImport={access.researchEdit} canCreateMenu={access.menuEdit} navigate={navigate} />
        {navButton('menus', t('nav.menus'), access.menuView)}
        {navButton('more', t('nav.more'))}
      </nav>
    </div>
  </div>;
}
